import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { AppDispatch } from '../../redux';
import { saveAllClinicians } from '../../redux/clinic/clinicSlice';
import { scrapedMemberToClinician } from '../../utils/scraperRun';
import { ScrapedTeamMember } from '../../redux/scraper/scraperSlice';
import api from '../../redux/api';

// Make webhook that emails invitations to newly added clinicians
const INVITATION_WEBHOOK_URL = 'https://hook.us2.make.com/v1kxpb947qy5tvjtnhnqi3a8xl8v0bex';

interface PendingScrapedClinicians {
  practiceId: string;
  practiceName?: string;
  members: ScrapedTeamMember[];
}

interface PendingPurchaseBilling {
  productId?: string;
  priceId?: string;
  totalAmount?: number;
  couponCode?: string;
  paymentMethod?: { type?: string; last4?: string };
  timeStarted?: number;
  skipInvitationEmail?: boolean;
}

const readJson = <T,>(key: string): T | null => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};

const formatPaymentMethod = (type?: string): string => {
  if (!type) return 'Credit Card';
  return type
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

/**
 * Completes a Workforce clinician purchase after Stripe payment succeeds:
 * creates the clinician records, records the transaction, and sends invitations.
 *
 * Reads its inputs from localStorage (written before payment, because Stripe's
 * redirect loses React state) and clears them on success — so it is safe to call
 * from both PaymentSuccessPage and the Workforce page; whichever runs second no-ops.
 */
export const useFinalizeClinicianPurchase = () => {
  const dispatch = useDispatch<AppDispatch>();
  const inFlightRef = useRef(false);

  const finalizePurchase = useCallback(async (): Promise<{ insertedCount: number } | null> => {
    if (inFlightRef.current) return null;

    const pending = readJson<PendingScrapedClinicians>('pendingScrapedClinicians');
    if (!pending?.practiceId || !Array.isArray(pending.members) || pending.members.length === 0) {
      return null; // nothing to finalize (already handled, or a legacy payment flow)
    }

    inFlightRef.current = true;
    try {
      const billing = readJson<PendingPurchaseBilling>('pendingPurchaseBilling');
      const paymentData = readJson<{ subscriptionId?: string }>('pendingPaymentData');

      // 1. Create the clinician records (status "new", inactive)
      const docs = pending.members.map(m =>
        scrapedMemberToClinician(m, pending.practiceId, { subscriptionId: paymentData?.subscriptionId })
      );
      const result: any = await dispatch(saveAllClinicians(docs as any)).unwrap();
      const insertedCount = Number(result?.insertedCount ?? 0);
      const insertedIds: string[] = Array.isArray(result?.insertedIds) ? result.insertedIds : [];

      if (insertedCount === 0 || insertedIds.length === 0) {
        throw new Error('No clinicians were created. Please contact support.');
      }

      // 2. Record the transaction (best-effort; the payment itself already succeeded)
      const now = Math.floor(Date.now() / 1000);
      try {
        await api.post('/transaction', {
          id_product: billing?.productId || '',
          product: 'Membership',
          total_amount: billing?.totalAmount ?? 0,
          total_amount_int: Math.round((billing?.totalAmount ?? 0) * 100),
          status: 'success',
          payment_details: {
            payment_method: formatPaymentMethod(billing?.paymentMethod?.type),
            last_four_digits: billing?.paymentMethod?.last4 || ''
          },
          clinician_ids: insertedIds,
          time_started: billing?.timeStarted ?? now,
          time_completed: now
        });
      } catch (err) {
        console.error('Failed to save transaction record:', err);
        toast.warning('Clinicians were added, but recording the transaction failed.');
      }

      // 3. Send invitations via the Make webhook, unless the user opted out.
      // Raw fetch: an external URL must not go through the axios instance / auth header.
      if (!billing?.skipInvitationEmail) {
        try {
          await fetch(INVITATION_WEBHOOK_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id_practice: pending.practiceId,
              practice_name: pending.practiceName || '',
              invitations: pending.members.slice(0, insertedIds.length).map((m, i) => {
                const nameParts = (m.name || '').trim().split(/\s+/);
                return {
                  id: insertedIds[i],
                  firstName: nameParts[0] || '',
                  lastName: nameParts.slice(1).join(' '),
                  email: m.email || ''
                };
              })
            })
          });
        } catch (err) {
          console.error('Failed to send invitations:', err);
          toast.warning('Clinicians were added, but sending invitations failed.');
        }
      }

      localStorage.removeItem('pendingScrapedClinicians');
      localStorage.removeItem('pendingPurchaseBilling');

      toast.success(`Successfully added ${insertedCount} clinician${insertedCount !== 1 ? 's' : ''} to your practice!`);
      return { insertedCount };
    } finally {
      inFlightRef.current = false;
    }
  }, [dispatch]);

  return { finalizePurchase };
};

export default useFinalizeClinicianPurchase;
